import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../api/config';
import '../styles/HODDashboard.css';
import MathRenderer, { hasMath } from '../components/MathRenderer';

function MathOrText({ text, subjectId, block = false, className = '' }) {
  if (!text) return null;
  if (hasMath(text, subjectId)) return <MathRenderer text={text} subjectId={subjectId} block={block} className={className} />;
  return <span className={className}>{text}</span>;
}

const JIT_LOGO = '/images/jit-logo.png';

function getUser() {
  try { return JSON.parse(localStorage.getItem('user') || '{}'); }
  catch { return {}; }
}

function subjectIcon(name = '') {
  const n = name.toLowerCase();
  if (n.includes('chem'))  return '⚗️';
  if (n.includes('phys'))  return '⚡';
  if (n.includes('math'))  return '📐';
  if (n.includes('bio'))   return '🧬';
  if (n.includes('comp') || n.includes('cs')) return '💻';
  return '📚';
}

function toTitleCase(str) {
  if (!str) return '';
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

// ── Module Select with "Others / create new" ──────────────────────────────────
function ModuleSelect({ value, subjectId, modules, onChange, onModuleCreated }) {
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  const subjectIdStr = subjectId != null ? String(subjectId) : '';
  const filtered     = modules.filter(m =>
    !subjectIdStr || String(m.subject_id) === subjectIdStr
  );
  const displayVal = showNew ? '__others__' : (value || '');

  const handleChange = (e) => {
    if (e.target.value === '__others__') { setShowNew(true); setNewName(''); setError(''); }
    else { setShowNew(false); onChange(e.target.value); }
  };

  const handleCreate = async () => {
    if (!newName.trim()) { setError('Enter a module name'); return; }
    if (!subjectIdStr)   { setError('No subject found — contact admin'); return; }
    setSaving(true); setError('');
    try {
      const res = await API.post('/api/verify/modules', {
        name: newName.trim(),
        subject_id: subjectIdStr,
      });
      const mod = res.data.module;
      onModuleCreated(mod);
      onChange(String(mod.module_id));
      setShowNew(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create');
    } finally { setSaving(false); }
  };

  return (
    <div>
      <select className="hod-form-input" value={displayVal} onChange={handleChange}>
        <option value="">— Select —</option>
        {filtered.map(m => (
          <option key={m.module_id} value={String(m.module_id)}>{m.name}</option>
        ))}
        <option value="__others__">Others (create new…)</option>
      </select>
      {showNew && (
        <div className="hod-module-new">
          <input
            className="hod-form-input hod-module-new-input"
            placeholder="New module name"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleCreate()}
          />
          <button className="hod-btn hod-btn--primary hod-btn--sm"
            onClick={handleCreate} disabled={saving}>
            {saving ? '…' : 'Add'}
          </button>
          <button className="hod-btn hod-btn--sm"
            onClick={() => { setShowNew(false); onChange(value); }}>
            Cancel
          </button>
          {error && <span className="hod-err-inline">{error}</span>}
        </div>
      )}
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────────────
function Sidebar({ active, setActive, user, onLogout }) {
  const dept = user?.department || 'Department';
  const nav  = [
    { key: 'dashboard', label: '📊 Dashboard'         },
    { key: 'pending',   label: '✅ Verified Questions' },
    { key: 'history',   label: '🏆 Approved History'  },
  ];
  return (
    <div className="hod-sidebar">
      <div className="hod-sidebar-brand">
        <div className="hod-brand-row">
          <img src={JIT_LOGO} alt="JIT" className="hod-sidebar-logo"
            onError={e => (e.target.style.display = 'none')} />
          <span className="hod-sidebar-jast">JAST</span>
        </div>
        <p className="hod-sidebar-college">Jyothy Institute of Technology</p>
        <div className="hod-dept-chip">{dept}</div>
      </div>
      <ul className="hod-sidebar-menu">
        {nav.map(n => (
          <li key={n.key}
            className={`hod-menu-item ${active === n.key ? 'hod-menu-item--active' : ''}`}
            onClick={() => setActive(n.key)}>
            {n.label}
          </li>
        ))}
      </ul>
      <div className="hod-sidebar-footer">
        <div className="hod-sidebar-user">
          <div className="hod-avatar">{user?.name?.charAt(0)?.toUpperCase()}</div>
          <div className="hod-user-info">
            <p className="hod-user-name">{user?.name}</p>
            <span className="hod-role-badge">HOD</span>
          </div>
          <button className="hod-logout-btn" onClick={onLogout} title="Logout">⬅</button>
        </div>
      </div>
    </div>
  );
}

// ── Dashboard Tab ─────────────────────────────────────────────────────────────
function DashboardTab({ user }) {
  const [stats,        setStats]        = useState({ total: 0, verified: 0, approved: 0, pending_approval: 0, subjects: [] });
  const [facultyStats, setFacultyStats] = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    Promise.all([
      API.get('/api/hod/stats'),
      API.get('/api/hod/faculty-stats'),
    ])
      .then(([statsRes, facultyRes]) => {
        setStats(statsRes.data);
        setFacultyStats(facultyRes.data || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="hod-page">
      <div className="hod-welcome">
        <div className="hod-welcome-left">
          <div className="hod-welcome-avatar">{user?.name?.charAt(0)?.toUpperCase()}</div>
          <div>
            <h1 className="hod-welcome-title">Welcome, {user?.name}!</h1>
            <p className="hod-welcome-sub">{user?.department} Department — Question Bank Overview</p>
          </div>
        </div>
        <div className="hod-welcome-role">🎓 Head Of Department</div>
      </div>

      {loading ? <div className="hod-loading">Loading stats…</div> : (
        <>
          {facultyStats.length > 0 && facultyStats.map((subject) => {
            const pendingApproval = (subject.verified || 0) - (subject.approved || 0);
            const remaining       = (subject.total    || 0) - (subject.approved || 0);
            return (
              <div key={subject.subject_id} className="hod-section">
                <div className="hod-section-header">
                  <span className="hod-section-title">
                    {subjectIcon(subject.name)} {subject.name} Verification Report
                  </span>
                </div>

                <div className="hod-sub-kpi-row hod-sub-kpi-row--five">
                  <div className="hod-sub-kpi-card hod-sub-kpi--blue">
                    <div className="hod-sub-kpi-icon">📝</div>
                    <div className="hod-sub-kpi-body">
                      <span className="hod-sub-kpi-label">Total Questions</span>
                      <span className="hod-sub-kpi-value">{(subject.total || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="hod-sub-kpi-card hod-sub-kpi--green">
                    <div className="hod-sub-kpi-icon">✅</div>
                    <div className="hod-sub-kpi-body">
                      <span className="hod-sub-kpi-label">Verified</span>
                      <span className="hod-sub-kpi-value">{(subject.verified || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="hod-sub-kpi-card hod-sub-kpi--amber">
                    <div className="hod-sub-kpi-icon">⏳</div>
                    <div className="hod-sub-kpi-body">
                      <span className="hod-sub-kpi-label">Pending Your Approval</span>
                      <span className="hod-sub-kpi-value">{pendingApproval.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="hod-sub-kpi-card hod-sub-kpi--purple">
                    <div className="hod-sub-kpi-icon">🛡️</div>
                    <div className="hod-sub-kpi-body">
                      <span className="hod-sub-kpi-label">HOD Approved</span>
                      <span className="hod-sub-kpi-value">{(subject.approved || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="hod-sub-kpi-card hod-sub-kpi--teal">
                    <div className="hod-sub-kpi-icon">📦</div>
                    <div className="hod-sub-kpi-body">
                      <span className="hod-sub-kpi-label">Remaining</span>
                      <span className="hod-sub-kpi-value">{remaining.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                <div className="hod-faculty-table-card">
                  <div className="hod-faculty-table-header">
                    <span className="hod-faculty-table-icon">🗒️</span>
                    <span className="hod-faculty-table-title">Verification Summary</span>
                  </div>

                  {subject.faculty.length === 0 ? (
                    <div className="hod-empty" style={{ padding: '32px 20px' }}>
                      <div className="hod-empty-icon" style={{ fontSize: 32 }}>📂</div>
                      <p className="hod-empty-title" style={{ fontSize: 14 }}>No Faculty Data Yet</p>
                      <p className="hod-empty-sub">No faculty has verified questions in this subject yet.</p>
                    </div>
                  ) : (
                    <div className="hod-faculty-table-scroll">
                      <table className="hod-faculty-table">
                        <colgroup>
                          <col className="hod-faculty-col-no" />
                          <col className="hod-faculty-col-name" />
                          <col className="hod-faculty-col-count" />
                          <col className="hod-faculty-col-count" />
                        </colgroup>
                        <thead>
                          <tr>
                            <th className="hod-faculty-col-no"    rowSpan={2}>Sl. No.</th>
                            <th className="hod-faculty-col-name"  rowSpan={2}>Faculty Name</th>
                            <th className="hod-faculty-col-group" colSpan={2}>No. of Questions</th>
                          </tr>
                          <tr>
                            <th className="hod-faculty-col-count">Verified</th>
                            <th className="hod-faculty-col-count">Approved</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subject.faculty.map((f, i) => (
                            <tr key={f.user_id}>
                              <td className="hod-faculty-col-no hod-faculty-td-no">{i + 1}</td>
                              <td className="hod-faculty-col-name">{f.name}</td>
                              <td className="hod-faculty-col-count hod-faculty-td-count">{f.verified}</td>
                              <td className="hod-faculty-col-count hod-faculty-td-count">{f.approved}</td>
                            </tr>
                          ))}
                          <tr className="hod-faculty-total-row">
                            <td className="hod-faculty-col-no"></td>
                            <td className="hod-faculty-col-name">Total</td>
                            <td className="hod-faculty-col-count hod-faculty-td-count">
                              {subject.faculty.reduce((s, f) => s + parseInt(f.verified || 0), 0)}
                            </td>
                            <td className="hod-faculty-col-count hod-faculty-td-count">
                              {subject.faculty.reduce((s, f) => s + parseInt(f.approved || 0), 0)}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

// ── Verified Questions Tab ────────────────────────────────────────────────────
function PendingApprovalTab() {
  const [questions,   setQuestions]   = useState([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [totalPages,  setTotalPages]  = useState(1);
  const [loading,     setLoading]     = useState(false);
  const [approving,   setApproving]   = useState({});
  const [editingQ,    setEditingQ]    = useState(null);
  const [modules,     setModules]     = useState([]);
  const [msg,         setMsg]         = useState({ text: '', ok: true });

  useEffect(() => {
    API.get('/api/hod/modules').then(r => setModules(r.data || [])).catch(() => {});
  }, []);

  const showMsg = (text, ok = true) => {
    setMsg({ text, ok });
    setTimeout(() => setMsg({ text: '', ok: true }), 3500);
  };

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const res = await API.get('/api/hod/verified-questions', { params: { page: pg, limit: 10 } });
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(pg);
    } catch { setQuestions([]); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(1); }, []); // eslint-disable-line

  const handleApprove = async (question_id) => {
    setApproving(a => ({ ...a, [question_id]: true }));
    try {
      await API.post('/api/hod/approve', { question_id });
      showMsg('✅ Question approved successfully!');
      setQuestions(qs => qs.filter(q => q.question_id !== question_id));
      setTotal(t => t - 1);
    } catch (err) {
      showMsg('❌ ' + (err.response?.data?.message || 'Approval failed'), false);
    } finally {
      setApproving(a => ({ ...a, [question_id]: false }));
    }
  };

  const handleModuleCreated = (mod) =>
    setModules(prev => prev.find(m => m.module_id === mod.module_id) ? prev : [...prev, mod]);

  return (
    <div className="hod-page">
      <div className="hod-page-header">
        <div>
          <h2 className="hod-page-title">Verified Questions — Pending Your Approval</h2>
          <p className="hod-page-sub">Content authors have verified these. Review and approve to finalise.</p>
        </div>
      </div>

      {msg.text && (
        <div className={`hod-flash ${msg.ok ? 'hod-flash--ok' : 'hod-flash--err'}`}>{msg.text}</div>
      )}

      <div className="hod-filter-row">
        <span className="hod-count-label">{total} question{total !== 1 ? 's' : ''} pending</span>
      </div>

      {loading && <div className="hod-loading">Loading…</div>}

      {!loading && questions.length === 0 && (
        <div className="hod-empty">
          <div className="hod-empty-icon">🎉</div>
          <p className="hod-empty-title">All Caught Up!</p>
          <p className="hod-empty-sub">No verified questions pending your approval.</p>
        </div>
      )}

      {!loading && questions.length > 0 && (
        <div className="hod-q-list">
          {questions.map((q) => (
            <div key={q.question_id} className="hod-q-card">
              <div className="hod-q-top">
                <div className="hod-q-tags">
                  {q.subject_name     && <span className="hod-tag hod-tag--blue">{q.subject_name}</span>}
                  {q.module_name      && <span className="hod-tag hod-tag--grey">{q.module_name}</span>}
                  {q.difficulty_level && (
                    <span className={`hod-tag hod-tag--diff hod-tag--${q.difficulty_level}`}>
                      {toTitleCase(q.difficulty_level)}
                    </span>
                  )}
                  {q.exam_type  && <span className="hod-tag hod-tag--grey">{q.exam_type}</span>}
                  {(q.exam_year || q.kcet_year) && (
                    <span className="hod-tag hod-tag--grey">{q.exam_year || q.kcet_year}</span>
                  )}
                </div>
                <span className="hod-verified-by">✅ Verified By: <strong>{q.verified_by_name || 'Author'}</strong></span>
              </div>

              <p className="hod-q-text">
                <span className="hod-q-num hod-q-num--inline">Q{q.question_number ?? q.question_id}.</span>
                <MathOrText text={q.question_text} subjectId={q.subject_id} block={false} />
              </p>

              <div className="hod-q-opts hod-q-opts--grid">
                {['a','b','c','d'].map(opt => {
                  const val     = q[`option_${opt}`];
                  if (!val) return null;
                  const correct = (q.correct_answer || '').toLowerCase() === opt;
                  return (
                    <div key={opt} className={`hod-opt ${correct ? 'hod-opt--correct' : ''}`}>
                      <span className="hod-opt-key">{opt.toLowerCase()})</span>
                      <span className="hod-opt-val"><MathOrText text={val} subjectId={q.subject_id} /></span>
                      {correct && <span className="hod-correct-mark">✓</span>}
                    </div>
                  );
                })}
              </div>

              {q.explanation && (
                <div className="hod-q-explanation">
                  <span className="hod-exp-label">Explanation:</span>{' '}
                  <MathOrText text={q.explanation} subjectId={q.subject_id} />
                </div>
              )}

              <div className="hod-q-actions">
                <button className="hod-btn hod-btn--edit"
                  onClick={() => setEditingQ(q)}
                  style={{ marginRight: 8 }}>
                  ✏️ Edit
                </button>
                <button className="hod-btn hod-btn--approve"
                  onClick={() => handleApprove(q.question_id)}
                  disabled={approving[q.question_id]}>
                  {approving[q.question_id] ? 'Approving…' : '🏆 Approve'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="hod-pagination">
          <button className="hod-btn" onClick={() => load(page - 1)} disabled={page === 1 || loading}>← Prev</button>
          <span className="hod-page-info">Page {page} of {totalPages}</span>
          <button className="hod-btn" onClick={() => load(page + 1)} disabled={page === totalPages || loading}>Next →</button>
        </div>
      )}

      {editingQ && (
        <EditModal
          question={editingQ}
          modules={modules}
          onModuleCreated={handleModuleCreated}
          onClose={() => setEditingQ(null)}
          onSaved={() => { setEditingQ(null); load(page); }}
        />
      )}
    </div>
  );
}

// ── Feedback Modal ────────────────────────────────────────────────────────────
function FeedbackModal({ question, existingFeedback, onClose, onSaved }) {
  const [note,    setNote]    = useState(existingFeedback?.note || '');
  const [pdfFile, setPdfFile] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const fileRef  = useRef(null);
  const API_BASE = API.defaults.baseURL;

  const handleSave = async () => {
    if (!note.trim() && !pdfFile) { setErr('Please add a note or upload a PDF.'); return; }
    setSaving(true); setErr('');
    try {
      const formData = new FormData();
      formData.append('question_id', question.question_id);
      formData.append('note', note.trim());
      if (pdfFile) formData.append('feedback_pdf', pdfFile);
      const res = await API.post('/api/verify/questions/feedback', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved({ note: note.trim(), hasPdf: !!pdfFile, feedback_pdf: res.data.feedback_pdf || null });
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="hod-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="hod-modal" style={{ maxWidth: 560 }}>
        <div className="hod-modal-header">
          <span className="hod-modal-title">💬 Feedback For Q{question.question_number ?? question.question_id}</span>
          <button className="hod-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="hod-modal-body">
          <div style={{
            background: '#f8fafc', borderLeft: '3px solid #6366f1',
            borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: 4,
          }}>
            <span className="hod-form-label" style={{ marginBottom: 4, display: 'block' }}>Question</span>
            {hasMath(question.question_text, question.subject_id)
              ? <MathRenderer text={question.question_text} subjectId={question.subject_id} block={true} />
              : <p style={{ margin: '4px 0 0', fontSize: 15, color: '#1a1a2e', lineHeight: 1.6 }}>{question.question_text}</p>
            }
          </div>

          <div className="hod-form-field">
            <label className="hod-form-label">Your Note / Remark</label>
            <textarea
              className="hod-form-input hod-form-textarea"
              rows={5}
              placeholder="Scribble down your feedback, corrections, or remarks..."
              value={note}
              onChange={e => setNote(e.target.value)}
            />
          </div>

          <div className="hod-form-field">
            <label className="hod-form-label">Attach PDF (Optional)</label>
            <div
              style={{
                border: '2px dashed #cbd5e1', borderRadius: 10, padding: '18px 14px',
                cursor: 'pointer', background: '#f8fafc', transition: 'all 0.15s',
              }}
              onClick={() => fileRef.current?.click()}
            >
              {pdfFile ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, color: '#1a1a2e' }}>
                  <span>📄</span>
                  <span style={{ flex: 1, wordBreak: 'break-all' }}>{pdfFile.name}</span>
                  <button
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14 }}
                    onClick={e => { e.stopPropagation(); setPdfFile(null); }}>✕</button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#1e293b' }}>
                  <span style={{ fontSize: 22 }}>📎</span>
                  <span style={{ fontSize: 14, fontWeight: 700 }}>Click To Upload A PDF</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>Max 10 MB</span>
                </div>
              )}
              <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                onChange={e => { if (e.target.files[0]) setPdfFile(e.target.files[0]); }} />
            </div>
            {existingFeedback?.feedback_pdf && !pdfFile && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                color: '#000', marginTop: 6, padding: '6px 10px',
                background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
              }}>
                <span>📄</span>
                <span>Previously Uploaded PDF On File</span>
                <span style={{ color: '#000' }}>(Uploading a new one will replace it)</span>
              </div>
            )}
            {existingFeedback?.feedback_pdf && (
              <div style={{ marginTop: 6 }}>
                <a
                  href={`${API_BASE}${existingFeedback.feedback_pdf}`}
                  className="hod-pdf-link"
                  onClick={e => {
                    e.preventDefault();
                    window.open(`${API_BASE}${existingFeedback.feedback_pdf}`, '_blank', 'noopener,noreferrer');
                  }}
                >
                  📄 View Existing PDF
                </a>
              </div>
            )}
          </div>
        </div>

        <div className="hod-modal-footer">
          {err && <span className="hod-err-inline" style={{ marginRight: 'auto' }}>{err}</span>}
          <button className="hod-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="hod-btn hod-btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save Feedback'}
          </button>
        </div>
      </div>
    </div>
  );

}



  
// ── Feedback Modal (styled, read-only capable) ───────────────────────────────
function FeedbackModalStyled({ question, existingFeedback, onClose, onSaved, readOnly = false }) {
  const [note,    setNote]    = useState(existingFeedback?.note || '');
  const [pdfFile, setPdfFile] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [err,     setErr]     = useState('');
  const fileRef  = useRef(null);
  const API_BASE = API.defaults.baseURL;

  const handleSave = async () => {
    if (!note.trim() && !pdfFile) { setErr('Please add a note or upload a PDF.'); return; }
    setSaving(true); setErr('');
    try {
      const formData = new FormData();
      formData.append('question_id', question.question_id);
      formData.append('note', note.trim());
      if (pdfFile) formData.append('feedback_pdf', pdfFile);
      const res = await API.post('/api/verify/questions/feedback', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onSaved({ note: note.trim(), hasPdf: !!pdfFile, feedback_pdf: res.data.feedback_pdf || null });
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="hod-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="hod-modal" style={{ maxWidth: 560 }}>
        <div className="hod-modal-header">
          <span className="hod-modal-title">
            {readOnly ? '👁️ Feedback — ' : '💬 Feedback For '}
            Q{question.question_number ?? question.question_id}
            {readOnly && (
              <span style={{
                marginLeft: 10, fontSize: 11, fontWeight: 700,
                background: '#fef3c7', color: '#92400e',
                border: '1px solid #fde68a', borderRadius: 6,
                padding: '2px 8px', verticalAlign: 'middle',
              }}>Read Only</span>
            )}
          </span>
          <button className="hod-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="hod-modal-body">
          {readOnly && question.reverification_note && (
            <div style={{
              background: '#fffbeb', border: '1px solid #fcd34d',
              borderRadius: 8, padding: '10px 14px', marginBottom: 4,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#92400e', display: 'block', marginBottom: 4 }}>
                📝 Admin Note (Why This Was Sent Back)
              </span>
              <p style={{ margin: 0, fontSize: 14, color: '#1e293b', lineHeight: 1.6 }}>
                {question.reverification_note}
              </p>
            </div>
          )}

          <div style={{
            background: '#f8fafc', borderLeft: '3px solid #6366f1',
            borderRadius: '0 8px 8px 0', padding: '10px 14px', marginBottom: 4,
          }}>
            <span className="hod-form-label" style={{ marginBottom: 4, display: 'block' }}>Question</span>
            {hasMath(question.question_text, question.subject_id)
              ? <MathRenderer text={question.question_text} subjectId={question.subject_id} block={true} />
              : <p style={{ margin: '4px 0 0', fontSize: 15, color: '#1a1a2e', lineHeight: 1.6 }}>{question.question_text}</p>
            }
          </div>

          <div className="hod-form-field">
            <label className="hod-form-label">
              {readOnly ? 'Previously Saved Note' : 'Your Note / Remark'}
            </label>
            {readOnly ? (
              <div style={{
                background: '#f8fafc', border: '1px solid #e2e8f0',
                borderRadius: 8, padding: '10px 14px',
                fontSize: 14, color: note ? '#1e293b' : '#94a3b8',
                lineHeight: 1.6, minHeight: 80,
              }}>
                {note || 'No note saved.'}
              </div>
            ) : (
              <textarea
                className="hod-form-input hod-form-textarea"
                rows={5}
                placeholder="Scribble down your feedback, corrections, or remarks..."
                value={note}
                onChange={e => setNote(e.target.value)}
              />
            )}
          </div>

          <div className="hod-form-field">
            <label className="hod-form-label">
              {readOnly ? 'Attached PDF' : 'Attach PDF (Optional)'}
            </label>
            {readOnly ? (
existingFeedback?.feedback_pdf ? (

<a href={`${API_BASE}${existingFeedback.feedback_pdf}`}
className="hod-pdf-link"
onClick={e => {
e.preventDefault();
window.open(`${API_BASE}${existingFeedback.feedback_pdf}`, '_blank', 'noopener,noreferrer');
                  }}
                >
                  📄 View Saved PDF
                </a>
              ) : (
                <p style={{ fontSize: 13, color: '#94a3b8', margin: 0 }}>No PDF attached.</p>
              )
            ) : (
              <>
                <div
                  style={{
                    border: '2px dashed #cbd5e1', borderRadius: 10, padding: '18px 14px',
                    cursor: 'pointer', background: '#f8fafc', transition: 'all 0.15s',
                  }}
                  onClick={() => fileRef.current?.click()}
                >
                  {pdfFile ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 15, color: '#1a1a2e' }}>
                      <span>📄</span>
                      <span style={{ flex: 1, wordBreak: 'break-all' }}>{pdfFile.name}</span>
                      <button
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14 }}
                        onClick={e => { e.stopPropagation(); setPdfFile(null); }}>✕</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: '#1e293b' }}>
                      <span style={{ fontSize: 22 }}>📎</span>
                      <span style={{ fontSize: 14, fontWeight: 700 }}>Click To Upload A PDF</span>
                      <span style={{ fontSize: 11, color: '#94a3b8' }}>Max 10 MB</span>
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files[0]) setPdfFile(e.target.files[0]); }} />
                </div>
                {existingFeedback?.feedback_pdf && !pdfFile && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 6, fontSize: 12,
                    color: '#000', marginTop: 6, padding: '6px 10px',
                    background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6,
                  }}>
                    <span>📄</span>
                    <span>Previously Uploaded PDF On File</span>
                    <span style={{ color: '#000' }}>(Uploading a new one will replace it)</span>
                  </div>
                )}
                 {existingFeedback?.feedback_pdf && (
<div style={{ marginTop: 6 }}>
                    
                    <a  href={`${API_BASE}${existingFeedback.feedback_pdf}`}
                      className="hod-pdf-link"
                      onClick={e => {
e.preventDefault();
window.open(`${API_BASE}${existingFeedback.feedback_pdf}`, '_blank', 'noopener,noreferrer');
                      }}
                    >
                      📄 View Existing PDF
                    </a>
                  </div>
                )}
                
              </>
            )}
          </div>
        </div>

        <div className="hod-modal-footer">
          {err && <span className="hod-err-inline" style={{ marginRight: 'auto' }}>{err}</span>}
          <button className="hod-btn" onClick={onClose} disabled={saving}>{readOnly ? 'Close' : 'Cancel'}</button>
          {!readOnly && (
            <button className="hod-btn hod-btn--primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Save Feedback'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}






// ── Edit Modal ────────────────────────────────────────────────────────────────
function EditModal({ question, modules, onModuleCreated, onClose, onSaved }) {
  const isReadOnly = (question.subject_name || '').toLowerCase().includes('math') ||
                     (question.subject_name || '').toLowerCase().includes('phys') ||
                     (question.subject_name || '').toLowerCase().includes('chem') ||
                     [1, 2, 3].includes(Number(question.subject_id));

  const BLOOMS_OPTS = [
    { value:'remember',   label:'1 - Remember'  },
    { value:'understand', label:'2 - Understand' },
    { value:'apply',      label:'3 - Apply'      },
    { value:'analyse',    label:'4 - Analyse'    },
    { value:'evaluate',   label:'5 - Evaluate'   },
    { value:'create',     label:'6 - Create'     },
  ];
  const OPT_KEYS   = ['a','b','c','d'];
  const OPT_FIELDS = ['option_a','option_b','option_c','option_d'];

  const [form, setForm] = useState({
    question_text   : question.question_text    || '',
    option_a        : question.option_a         || '',
    option_b        : question.option_b         || '',
    option_c        : question.option_c         || '',
    option_d        : question.option_d         || '',
    correct_answer  : (question.correct_answer  || 'A').toUpperCase(),
    explanation     : question.explanation      || '',
    difficulty_level: question.difficulty_level || '',
    blooms_index    : question.blooms_index      || '',
    module_id       : String(question.module_id || ''),
  });
  const [saving,        setSaving]        = useState(false);
  const [err,           setErr]           = useState('');
  const [showFeedback,  setShowFeedback]  = useState(false);
  const [feedbackSaved, setFeedbackSaved] = useState(
    !!(question.feedback?.note || question.feedback?.feedback_pdf)
  );
  const [feedbackData, setFeedbackData] = useState(question.feedback || null);

  const f = (field, val) => setForm(prev => ({ ...prev, [field]: val }));

  const explRef = useRef(null);
  useEffect(() => {
    const el = explRef.current;
    if (el) {
      el.style.height = 'auto';
      el.style.height = el.scrollHeight + 'px';
    }
  }, [form.explanation]);

  const handleSave = async () => {
    if (!form.question_text.trim() && !isReadOnly) { setErr('Question text is required'); return; }
    setSaving(true); setErr('');
    try {
      await API.put(`/api/hod/questions/${question.question_id}`, {
        ...form,
        correct_answer  : form.correct_answer.toUpperCase(),
        blooms_index    : form.blooms_index || null,
        module_id       : form.module_id    || null,
      });
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  const curAnswer = form.correct_answer.toLowerCase();
  const curDiff   = form.difficulty_level;
 // const API_BASE  = API.defaults.baseURL;

  return (
    <div className="hod-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="hod-modal hod-edit-modal-v2">

        <div className="hod-modal-header hod-edit-head">
          <div className="hod-edit-head-left">
            <span>✏️</span>
            <span className="hod-edit-title">
              Edit — Q{question.question_number ?? question.question_id}
            </span>
          </div>
          <button className="hod-modal-close" onClick={onClose}>✕</button>
        </div>

        {err && <div className="hod-flash hod-flash--err" style={{ margin: '0 24px' }}>{err}</div>}

        <div className="hod-modal-body hod-edit-body">
          <div className="hod-edit-two-col-v2">

            {/* ── LEFT ── */}
            <div className="hod-edit-left-v2">

              <div className="hod-edit-section">
                <div className="hod-edit-field-label">Question</div>
                <div className="hod-edit-content-box hod-edit-content-box--ro">
                  {hasMath(form.question_text, question.subject_id)
                    ? <MathRenderer text={form.question_text} subjectId={question.subject_id} block={true} />
                    : <span className="hod-edit-plain-text">{form.question_text}</span>}
                </div>
              </div>

              <div className="hod-edit-section">
                <div className="hod-edit-field-label">Options</div>
                <div className="hod-edit-opts-grid-v2">
                  {OPT_KEYS.map((opt, i) => {
                    const fieldKey  = OPT_FIELDS[i];
                    const val       = form[fieldKey];
                    const isCorrect = curAnswer === opt;
                    return (
                      <div key={opt} className={`hod-edit-opt-cell-v2 ${isCorrect ? 'hod-edit-opt-cell-v2--correct' : ''}`}>
                        <span className="hod-edit-opt-lbl-v2">{opt.toLowerCase()})</span>
                        {isReadOnly ? (
                          <div className="hod-edit-opt-math">
                            <MathOrText text={val} subjectId={question.subject_id} />
                          </div>
                        ) : (
                          hasMath(val, question.subject_id) ? (
                            <div className="hod-edit-opt-math">
                              <MathOrText text={val} subjectId={question.subject_id} />
                            </div>
                          ) : (
                            <input
                              type="text"
                              className="hod-edit-opt-input-v2"
                              value={val}
                              placeholder={`Option ${opt.toUpperCase()}`}
                              onChange={e => f(fieldKey, e.target.value)}
                            />
                          )
                        )}
                        {isCorrect && <span className="hod-edit-opt-check-v2">✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="hod-edit-section">
                <div className="hod-edit-field-label">Explanation</div>
                {isReadOnly ? (
                  <div className="hod-edit-content-box hod-edit-content-box--ro">
                    <MathOrText text={form.explanation} subjectId={question.subject_id} block={true} />
                  </div>
                ) : hasMath(form.explanation, question.subject_id) ? (
                  <div className="hod-edit-content-box">
                    <MathOrText text={form.explanation} subjectId={question.subject_id} block={true} />
                  </div>
                ) : (
                  <textarea
                    ref={explRef}
                    className="hod-edit-textarea--expl"
                    value={form.explanation}
                    placeholder="Explanation…"
                    onChange={e => f('explanation', e.target.value)}
                    onInput={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                    }}
                  />
                )}
              </div>
            </div>

            {/* ── RIGHT ── */}
            <div className="hod-edit-right-v2">

              <div className="hod-edit-ctrl-field">
                <div className="hod-edit-ctrl-label">Module</div>
                <ModuleSelect
                  value={form.module_id}
                  subjectId={question.subject_id}
                  modules={modules}
                  onChange={val => f('module_id', val)}
                  onModuleCreated={mod => {
                    if (onModuleCreated) onModuleCreated(mod);
                    f('module_id', String(mod.module_id));
                  }}
                />
              </div>

              <div className="hod-edit-ctrl-field">
                <div className="hod-edit-ctrl-label">Correct Answer</div>
                <div className="hod-edit-ans-btns-v2">
                  {OPT_KEYS.map(opt => (
                    <button key={opt} type="button"
                      className={`hod-edit-ans-btn-v2 ${curAnswer === opt ? 'hod-edit-ans-btn-v2--active' : ''}`}
                      onClick={() => f('correct_answer', opt.toUpperCase())}>
                      {opt.toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hod-edit-ctrl-field">
                <div className="hod-edit-ctrl-label">Difficulty Level</div>
                <div className="hod-edit-diff-row-v2">
                  {['low','medium','high'].map(d => (
                    <button key={d} type="button"
                      className={`hod-edit-diff-btn hod-edit-diff-btn--${d} ${curDiff === d ? 'hod-edit-diff-btn--active' : ''}`}
                      onClick={() => f('difficulty_level', d)}>
                      {toTitleCase(d)}
                    </button>
                  ))}
                </div>
              </div>

              <div className="hod-edit-ctrl-field">
                <div className="hod-edit-ctrl-label">Bloom's Index</div>
                <select className="hod-form-input" value={form.blooms_index}
                  onChange={e => f('blooms_index', e.target.value)}>
                  <option value="">— Select —</option>
                  {BLOOMS_OPTS.map(b => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
              </div>

              {/* ── Feedback — read-only for reverification, editable otherwise ── */}
              <div className="hod-edit-ctrl-field">
                <div className="hod-edit-ctrl-label">Feedback</div>
                <button
                  type="button"
                  className="hod-btn hod-btn--sm"
                  style={
                    (question.needs_reverification === true || question.needs_reverification === 'true' ||
                     question.was_reverified === true || question.was_reverified === 'true')
                      ? { borderColor: '#fcd34d', color: '#92400e', background: '#fffbeb' }
                      : feedbackSaved
                        ? { borderColor: '#10b981', color: '#065f46', background: '#d1fae5' }
                        : { borderColor: '#cbd5e1', color: '#475569', background: '#f8fafc' }
                  }
                  onClick={() => setShowFeedback(true)}
                >
                  {(question.needs_reverification === true || question.needs_reverification === 'true' ||
                    question.was_reverified === true || question.was_reverified === 'true')
                    ? '👁️ View Feedback'
                    : (feedbackSaved ? '💬 Feedback ✓' : '💬 Feedback')}
                </button>
              </div>

            </div>
          </div>
        </div>

        <div className="hod-modal-footer">
          <button className="hod-btn" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="hod-btn hod-btn--approve" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : '💾 Save Changes'}
          </button>
        </div>
      </div>

{showFeedback && (
        <FeedbackModalStyled
question={question}
existingFeedback={feedbackData}
readOnly={
  question.needs_reverification === true || question.needs_reverification === 'true' ||
  question.was_reverified === true || question.was_reverified === 'true'
}
onClose={() => setShowFeedback(false)}
onSaved={(saved) => {
setFeedbackSaved(true);
setFeedbackData(prev => ({
              ...(prev || {}),
note: saved.note,
feedback_pdf: saved.feedback_pdf || (prev?.feedback_pdf ?? null),
            }));
setShowFeedback(false);
          }}
/>
      )}
</div>
  );
}

// ── Approved History Tab ──────────────────────────────────────────────────────
function ApprovedHistoryTab() {
  const [questions,  setQuestions]  = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [modules,    setModules]    = useState([]);
  const [editing,    setEditing]    = useState(null);

  useEffect(() => {
    API.get('/api/hod/modules').then(r => setModules(r.data || [])).catch(() => {});
  }, []);

  const handleModuleCreated = (mod) =>
    setModules(prev => prev.find(m => m.module_id === mod.module_id) ? prev : [...prev, mod]);

  const load = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const res = await API.get('/api/hod/approved-history', { params: { page: pg, limit: 10 } });
      setQuestions(res.data.questions || []);
      setTotal(res.data.total || 0);
      setTotalPages(res.data.totalPages || 1);
      setPage(pg);
    } catch { setQuestions([]); }
    finally  { setLoading(false); }
  }, []);

  useEffect(() => { load(1); }, []); // eslint-disable-line

  return (
    <div className="hod-page">
      <div className="hod-page-header">
        <div>
          <h2 className="hod-page-title">Approved History</h2>
          <p className="hod-page-sub">All questions you approved. Click Edit to make corrections.</p>
        </div>
      </div>

      <div className="hod-filter-row">
        <span className="hod-count-label">{total} approved question{total !== 1 ? 's' : ''}</span>
      </div>

      {loading && <div className="hod-loading">Loading…</div>}

      {!loading && questions.length === 0 && (
        <div className="hod-empty">
          <div className="hod-empty-icon">📋</div>
          <p className="hod-empty-title">No Approved Questions Yet.</p>
          <p className="hod-empty-sub">Go to "Verified Questions" tab to start approving.</p>
        </div>
      )}

      {!loading && questions.length > 0 && (
        <div className="hod-hist-table">
          <div className="hod-hist-hdr">
            <div className="hod-hist-col hod-hist-col--no">Sl. No.</div>
            <div className="hod-hist-col hod-hist-col--q">Question</div>
            <div className="hod-hist-col hod-hist-col--year">Year</div>
            <div className="hod-hist-col hod-hist-col--author">Verified By</div>
            <div className="hod-hist-col hod-hist-col--date">Approved On</div>
            <div className="hod-hist-col hod-hist-col--action">Action</div>
          </div>
          {questions.map((q) => (
            <div key={q.question_id} className="hod-hist-row">
              <div className="hod-hist-col hod-hist-col--no">{q.question_number ?? q.question_id}</div>
              <div className="hod-hist-col hod-hist-col--q">
                <p className="hod-hist-qtext">
                  {q.question_text?.length > 100
                    ? <MathOrText text={q.question_text.slice(0, 100) + '…'} subjectId={q.subject_id} />
                    : <MathOrText text={q.question_text} subjectId={q.subject_id} />}
                </p>
                {q.module_name && (
                  <span className="hod-tag hod-tag--grey hod-tag--sm">{q.module_name}</span>
                )}
              </div>
              <div className="hod-hist-col hod-hist-col--year">{q.exam_year || q.kcet_year || '—'}</div>
              <div className="hod-hist-col hod-hist-col--author">{q.verified_by_name || '—'}</div>
              <div className="hod-hist-col hod-hist-col--date">
                {q.hod_approved_at
                  ? new Date(q.hod_approved_at).toLocaleDateString('en-IN')
                  : '—'}
              </div>
              <div className="hod-hist-col hod-hist-col--action">
                <button className="hod-btn hod-btn--edit" onClick={() => setEditing(q)}>
                  ✏️ Edit
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="hod-pagination">
          <button className="hod-btn" onClick={() => load(page - 1)} disabled={page === 1 || loading}>← Prev</button>
          <span className="hod-page-info">Page {page} of {totalPages}</span>
          <button className="hod-btn" onClick={() => load(page + 1)} disabled={page === totalPages || loading}>Next →</button>
        </div>
      )}

      {editing && (
        <EditModal
          question={editing}
          modules={modules}
          onModuleCreated={handleModuleCreated}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(page); }}
        />
      )}
    </div>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function HODDashboard() {
  const navigate = useNavigate();
  const user     = getUser();
  const [active, setActive] = useState('dashboard');

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  return (
    <div className="hod-layout">
      <Sidebar active={active} setActive={setActive} user={user} onLogout={handleLogout} />
      <div className="hod-main">
        {active === 'dashboard' && <DashboardTab user={user} />}
        {active === 'pending'   && <PendingApprovalTab />}
        {active === 'history'   && <ApprovedHistoryTab />}
      </div>
    </div>
  );
}